import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TrendingUp, Activity, Flame, ArrowRight, Sparkles, ChevronRight, BarChart3, Gavel, Mail } from 'lucide-react';
import { motion } from 'framer-motion';
import { AreaChart, Area, ResponsiveContainer, Tooltip } from 'recharts';
import { supabase } from '../lib/supabase';

const staggerContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 }
  }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  show: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

const MarketCard = ({ title, value, change, isPositive, delay = 0 }: { title: string, value: string, change: string, isPositive: boolean, delay?: number }) => (
  <motion.div
    variants={fadeInUp}
    className="card-premium p-5 group cursor-pointer hover:scale-[1.02] transition-transform duration-300"
  >
    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-radial from-copper-500/10 to-transparent rounded-full blur-3xl -mr-16 -mt-16 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

    <div className="flex items-center justify-between mb-3">
      <span className="text-cream-200/60 text-xs font-medium uppercase tracking-[0.15em]">{title}</span>
      <div className={`w-2 h-2 rounded-full ${isPositive ? 'bg-success' : 'bg-danger'} animate-pulse`} />
    </div>

    <div className="text-3xl font-display font-semibold text-cream-50 mb-2 tracking-tight">{value}</div>

    <div className={`text-sm font-medium flex items-center gap-1.5 ${isPositive ? 'text-success' : 'text-danger'}`}>
      {isPositive ? <TrendingUp className="w-4 h-4" /> : <Activity className="w-4 h-4" />}
      <span>{change}</span>
    </div>
  </motion.div>
);

export const Home = () => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Fetch user profile
        const { data: profile } = await supabase
          .from('profiles')
          .select('display_name, avatar_url')
          .eq('id', user.id)
          .single();

        setAvatarUrl(profile?.avatar_url || null);

        // Fetch all cards for statistics
        const { data: cards, error: cardsError } = await supabase
          .from('cards')
          .select('*')
          .eq('user_id', user.id);

        if (cardsError) throw cardsError;

        // Fetch active auctions count
        const { count: activeAuctionsCount, error: auctionsError } = await supabase
          .from('auctions')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'active');

        if (auctionsError) throw auctionsError;

        // Calculate stats from cards
        const userCards = cards || [];
        const totalValue = userCards.reduce((sum, card) => sum + (Number(card.price) || 0), 0);
        const cardCount = userCards.length;
        const forSaleCount = userCards.filter(c => c.status === 'for_sale').length;

        // Build set breakdown
        const setMap: Record<string, { count: number; value: number }> = {};
        for (const card of userCards) {
          const setName = card.set_name || 'Unknown';
          if (!setMap[setName]) setMap[setName] = { count: 0, value: 0 };
          setMap[setName].count++;
          setMap[setName].value += Number(card.price) || 0;
        }
        const setBreakdown = Object.entries(setMap)
          .map(([name, data]) => ({ name, ...data }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 10);

        // Mock chart data (preserving existing logic)
        const chartData = Array(7).fill(0).map((_, i) => {
          const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
          const dayFactor = 0.95 + (i * 0.01) + (Math.sin(i * 0.5) * 0.02);
          return {
            name: dayNames[i],
            value: Math.round(totalValue * dayFactor * 100) / 100
          };
        });

        setStats({
          userName: profile?.display_name || user.email?.split('@')[0] || 'Collector',
          totalValue,
          cardCount,
          chartData,
          setBreakdown,
          activeAuctions: activeAuctionsCount || 0,
          forSaleCount
        });

      } catch (e) {
        console.error("Failed to fetch stats", e);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const dailyChange = stats?.chartData?.length >= 7
    ? stats.chartData[6].value - stats.chartData[5].value
    : 0;
  const dailyChangePercent = stats?.totalValue > 0
    ? ((dailyChange / stats.totalValue) * 100).toFixed(1)
    : "0.0";

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Atmospheric background */}
      <div className="fixed inset-0 bg-obsidian">
        <div className="absolute inset-0 bg-gradient-to-b from-copper-500/5 via-transparent to-transparent" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-radial from-copper-500/8 to-transparent rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-radial from-sage-500/5 to-transparent rounded-full blur-3xl -translate-x-1/2 translate-y-1/2" />
      </div>

      {/* Noise texture overlay */}
      <div className="fixed inset-0 opacity-[0.015] pointer-events-none" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
      }} />

      <motion.div
        className="relative z-10 p-5 pt-16 pb-32 space-y-8"
        variants={staggerContainer}
        initial="hidden"
        animate="show"
      >
        {/* Header */}
        <motion.header variants={fadeInUp} className="flex justify-between items-start">
          <div className="space-y-1">
            <h1 className="text-4xl font-display font-semibold text-cream-50 tracking-tight leading-tight">
              {loading ? (
                <span className="inline-block w-48 h-10 bg-white/5 rounded animate-pulse" />
              ) : (
                <>Welcome back,<br /><span className="text-gradient-gold">{stats?.userName?.split(' ')[0] || 'Collector'}</span></>
              )}
            </h1>
            <p className="text-cream-200/50 text-sm font-light tracking-wide">
              Your collection awaits
            </p>
          </div>

          <motion.div
            variants={scaleIn}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="relative"
          >
            <div className="w-14 h-14 rounded-2xl overflow-hidden border border-copper-400/30 shadow-elevated bg-copper-500/20 flex items-center justify-center">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="Profile"
                  className="w-full h-full object-cover"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              ) : (
                <span className="text-xl font-display font-bold text-copper-400">
                  {(stats?.userName || 'C')[0].toUpperCase()}
                </span>
              )}
            </div>
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-success rounded-full border-2 border-obsidian" />
          </motion.div>
        </motion.header>

        {/* Portfolio Value Card */}
        <motion.div variants={fadeInUp} className="card-premium p-6 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-copper-500/5 to-transparent" />

          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-xl bg-copper-500/20 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-copper-400" />
              </div>
              <span className="text-cream-200/60 text-sm font-medium">Total Portfolio Value</span>
            </div>

            <div className="flex items-end justify-between">
              <div>
                <div className="text-5xl font-display font-semibold text-cream-50 tracking-tight mb-1">
                  {loading ? (
                    <span className="inline-block w-40 h-14 bg-white/5 rounded animate-pulse" />
                  ) : (
                    `$${stats?.totalValue?.toLocaleString() || '0'}`
                  )}
                </div>
                <div className={`text-sm font-medium flex items-center gap-1 ${dailyChange >= 0 ? 'text-success' : 'text-danger'}`}>
                  <TrendingUp className="w-4 h-4" />
                  <span>{dailyChange >= 0 ? '+' : ''}{dailyChangePercent}% this week</span>
                </div>
              </div>

              <motion.button
                onClick={() => navigate('/collection')}
                whileHover={{ scale: 1.05, x: 5 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center gap-1 text-copper-400 text-sm font-medium"
              >
                Details <ChevronRight className="w-4 h-4" />
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <motion.div variants={fadeInUp} className="grid grid-cols-2 gap-4">
          <MarketCard
            title="Daily P&L"
            value={loading ? "—" : `${dailyChange >= 0 ? '+' : ''}$${Math.abs(dailyChange).toLocaleString()}`}
            change={`${dailyChangePercent}%`}
            isPositive={dailyChange >= 0}
          />
          <MarketCard
            title="Collection"
            value={loading ? "—" : (stats?.totalCards ?? stats?.cardCount ?? 0).toString()}
            change={stats?.totalCards > 0 ? "Cards" : "Start Scanning"}
            isPositive={true}
          />
        </motion.div>

        {/* Chart Section */}
        <motion.div variants={fadeInUp} className="card-premium p-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-copper-500/20 to-copper-600/10 flex items-center justify-center">
                <Flame className="w-5 h-5 text-copper-400" />
              </div>
              <div>
                <h3 className="font-semibold text-cream-50">Portfolio Trend</h3>
                <p className="text-cream-200/40 text-xs">Performance overview</p>
              </div>
            </div>
            <span className="text-xs text-cream-200/40 bg-white/5 px-3 py-1.5 rounded-full font-medium">Last 7 Days</span>
          </div>

          <div className="h-44 w-full">
            {loading ? (
              <div className="h-full flex items-center justify-center">
                <div className="flex gap-1">
                  {[0, 1, 2].map(i => (
                    <div key={i} className={`w-2 h-8 bg-copper-400/30 rounded-full animate-pulse`} style={{ animationDelay: `${i * 150}ms` }} />
                  ))}
                </div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats?.chartData || []}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#C9915D" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#C9915D" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1C1C1F',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: '12px',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
                    }}
                    itemStyle={{ color: '#F5F0E6' }}
                    labelStyle={{ color: '#A8A8A8' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#C9915D"
                    fillOpacity={1}
                    fill="url(#colorValue)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </motion.div>

        {/* Active Listings Summary */}
        {!loading && (stats?.activeAuctions > 0 || stats?.forSaleCount > 0) && (
          <motion.div variants={fadeInUp} className="grid grid-cols-2 gap-4">
            <div className="card-premium p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-danger/15 flex items-center justify-center">
                <Gavel className="w-5 h-5 text-danger" />
              </div>
              <div>
                <p className="text-2xl font-display font-semibold text-cream-50">{stats?.activeAuctions || 0}</p>
                <p className="text-cream-200/40 text-xs">Active Auctions</p>
              </div>
            </div>
            <div className="card-premium p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-success/15 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-display font-semibold text-cream-50">{stats?.forSaleCount || 0}</p>
                <p className="text-cream-200/40 text-xs">For Sale</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Set Breakdown */}
        {!loading && stats?.setBreakdown && stats.setBreakdown.length > 0 && (
          <motion.div variants={fadeInUp} className="card-premium p-6">
            <div className="flex justify-between items-center mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sage-500/20 to-sage-600/10 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-sage-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-cream-50">Collection by Set</h3>
                  <p className="text-cream-200/40 text-xs">Top sets in your collection</p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {stats.setBreakdown.slice(0, 5).map((set: any, i: number) => {
                const maxValue = stats.setBreakdown[0].value || 1;
                const pct = (set.value / maxValue) * 100;
                return (
                  <div key={set.name} className="space-y-1.5">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-cream-100 truncate max-w-[60%]">{set.name}</span>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="text-cream-200/40">{set.count} cards</span>
                        <span className="text-success font-semibold">${set.value.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-white/[0.04] rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.6, delay: i * 0.1 }}
                        className="h-full bg-gradient-to-r from-copper-500 to-copper-400 rounded-full"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Quick Actions */}
        <motion.div variants={fadeInUp} className="space-y-3">
          <h3 className="text-cream-200/60 text-xs font-medium uppercase tracking-[0.15em] px-1">Quick Actions</h3>

          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Scan Card', icon: '📷', href: '/scan' },
              { label: 'Browse Catalog', icon: '📚', href: '/catalog' },
              { label: 'Marketplace', icon: '🏪', href: '/market' },
              { label: 'My Collection', icon: '💎', href: '/collection' },
            ].map((action, i) => (
              <Link key={action.label} to={action.href}>
                <motion.div
                  variants={scaleIn}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="card-premium p-4 flex items-center gap-3 group cursor-pointer"
                >
                  <span className="text-2xl">{action.icon}</span>
                  <span className="text-cream-100 text-sm font-medium group-hover:text-copper-400 transition-colors">{action.label}</span>
                </motion.div>
              </Link>
            ))}
          </div>
        </motion.div>

        {/* Footer */}
        <motion.footer variants={fadeInUp} className="pt-12 pb-6 flex flex-col items-center gap-6 border-t border-white/[0.04]">
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-4 text-sm font-medium text-cream-200/60">
            <Link to="/terms" className="hover:text-cream-100 transition-colors">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-cream-100 transition-colors">Privacy Policy</Link>
            <Link to="/refund" className="hover:text-cream-100 transition-colors">Refund Policy</Link>
          </div>

          <a
            href="mailto:inman@mail.com"
            className="group flex items-center gap-2 px-5 py-2.5 bg-white/[0.03] hover:bg-white/[0.08] border border-white/[0.08] rounded-full text-sm font-medium text-cream-100 transition-all hover:scale-105"
          >
            <Mail className="w-4 h-4 text-copper-400 group-hover:text-cream-100 transition-colors" />
            <span>Contact Support</span>
          </a>

          <p className="text-xs text-cream-200/20">
            © {new Date().getFullYear()} CardBoss. All rights reserved.
          </p>
        </motion.footer>
      </motion.div>
    </div>
  );
};
